// ModalContext.tsx
import { ReactNode, createContext, useState } from 'react';
import { buildTaskUrl } from '../utils/urlUtils';
import { useActiveOrganization } from '../common/auth';
import EditStatusesModal from '../components/Modal/EditStatusesModal';
import { CreateItemModal } from '../components/Modal/CreateItemModal';
import { message } from 'antd';
import { CheckCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import Task from '../types/Task';
import { TaskType } from '../types/TaskType';
import { BaseButton } from '../components/UI/BaseButton';
import colorTokens from '../constants/design_system/colors';
import { useParams } from 'react-router-dom';
import { useGetWorkspace } from '../serverState/workspace';
import SelfSortModal from '../components/SelfSort/SelfSortModal';

export enum ModalType {
  EditStatuses = 'editStatuses',
  CreateItem = 'createItem',
  SelfSort = 'selfSort',
}

interface ModalState {
  isOpen: boolean;
  isLoading: boolean;
  props?: Record<string, any>;
}

interface ModalContextType {
  modals: Record<ModalType, ModalState>;
  openModal: (modalType: ModalType, props?: Record<string, any>) => void;
  closeModal: (modalType: ModalType) => void;
  setModalLoading: (modalType: ModalType, isLoading: boolean) => void;
}

const initialModalState: Record<ModalType, ModalState> = {
  [ModalType.EditStatuses]: { isOpen: false, isLoading: false },
  [ModalType.CreateItem]: { isOpen: false, isLoading: false },
  [ModalType.SelfSort]: { isOpen: false, isLoading: false },
};

export const ModalContext = createContext<ModalContextType>({
  modals: initialModalState,
  openModal: () => {},
  closeModal: () => {},
  setModalLoading: () => {},
});

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<Record<ModalType, ModalState>>(initialModalState);
  const [messageApi, contextHolder] = message.useMessage();
  const { data: activeOrganization } = useActiveOrganization();
  const { orgId, spaceId } = useParams();
  const workspaceQuery = useGetWorkspace();

  const openModal = (modalType: ModalType, props?: Record<string, any>) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { ...prev[modalType], isOpen: true, props },
    }));
  };

  const closeModal = (modalType: ModalType) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { ...prev[modalType], isOpen: false, props: undefined },
    }));
  };

  const setModalLoading = (modalType: ModalType, isLoading: boolean) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { ...prev[modalType], isLoading },
    }));
  };

  const showCreatedSuccessMessage = async (createdTask: Task) => {
    const taskTitle = createdTask.type === TaskType.epic ? 'Epic' : createdTask.parentTaskId ? 'Subitem' : 'Item';
    await messageApi.success({
      content: (
        <CreateSuccessMessage
          taskTitle={taskTitle}
          onGoToItem={() => {
            if (activeOrganization?.publicId) {
              window.location.href = buildTaskUrl({
                orgId: activeOrganization.publicId,
                spaceId: workspaceQuery.data?.publicId || createdTask.workspaceId,
                taskId: createdTask.taskId,
              });
            } else {
              // Fallback to old URL structure if no org context
              window.location.href = `/${orgId}/space/${spaceId}/task/${createdTask.taskId}`;
            }
          }}
        />
      ),
      duration: 5,
      style: {
        marginRight: '20px',
      },
    });
  };

  return (
    <ModalContext.Provider
      value={{
        modals,
        openModal,
        closeModal,
        setModalLoading,
      }}
    >
      {children}
      {contextHolder}
      {modals[ModalType.EditStatuses].isOpen && (
        <EditStatusesModal
          open={modals[ModalType.EditStatuses].isOpen}
          onCancel={() => closeModal(ModalType.EditStatuses)}
          confirmLoading={modals[ModalType.EditStatuses].isLoading}
          {...modals[ModalType.EditStatuses].props}
        />
      )}
      {modals[ModalType.CreateItem].isOpen && (
        <CreateItemModal
          open={modals[ModalType.CreateItem].isOpen}
          onCancel={() => closeModal(ModalType.CreateItem)}
          confirmLoading={modals[ModalType.CreateItem].isLoading}
          showMessage={showCreatedSuccessMessage}
          {...modals[ModalType.CreateItem].props}
        />
      )}
      {modals[ModalType.SelfSort].isOpen && (
        <SelfSortModal
          open={modals[ModalType.SelfSort].isOpen}
          onCancel={() => closeModal(ModalType.SelfSort)}
          variant="member"
          {...modals[ModalType.SelfSort].props}
        />
      )}
    </ModalContext.Provider>
  );
};

// Custom message component
const CreateSuccessMessage = ({ taskTitle, onGoToItem }: { taskTitle: string; onGoToItem: () => void }) => (
  <div className="flex items-center justify-between gap-4 min-w-[300px]">
    <div className="text-black text-base font-normal">{taskTitle} created</div>
    <BaseButton onClick={onGoToItem} type="secondary" size="small" className="text-xs">
      Go to {taskTitle.toLowerCase()}
    </BaseButton>
  </div>
);
